import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/app/providers/auth-context'
import { keepsStorefrontClub, storefrontReturnTarget } from '@/features/clubs/storefrontReturn'
import { StorefrontReturn } from './StorefrontReturn'
import { initialsFor } from './PublicAccountMenu'
import styles from './AppTopBar.module.css'

type Props = {
  onMenuOpen: () => void
}

/**
 * Phone / tablet top bar — visible at < 1024 px viewports. 48 px
 * tall, fixed to viewport top. Hamburger button on the left opens
 * the single merged AppDrawer; brand wordmark sits centred; the right
 * element is the account avatar — a profile shortcut (Option A). The
 * full account actions live in the drawer's ACCOUNT section, so the
 * avatar stays a lightweight jump, not a second menu. See
 * docs/ux/2026-07-18-nav-top-plus-sidebar.md §7.
 */
export function AppTopBar({ onMenuOpen }: Props) {
  const { state, me } = useAuth()
  const isAuthed = state === 'authenticated'
  const displayName = String(me?.player?.displayName || '').trim()
  // The board "Find a game" lands on keeps the club this tab walked out of (/ux
  // storefront-exits §3.1), in the brand slot; every other app page keeps our wordmark.
  const { pathname } = useLocation()
  const boardReturn = keepsStorefrontClub(pathname) ? storefrontReturnTarget(pathname) : null

  return (
    <header className={styles.topBar}>
      <button
        type="button"
        className={styles.menuButton}
        aria-label="Open navigation"
        aria-haspopup="dialog"
        onClick={onMenuOpen}
      >
        <HamburgerIcon />
      </button>
      {boardReturn ? (
        <span className={styles.clubSlot}>
          <StorefrontReturn target={boardReturn} />
        </span>
      ) : (
        <Link to="/" className={styles.brand}>
          The Racket Center
        </Link>
      )}
      {isAuthed ? (
        <Link to="/profile" className={styles.avatar} aria-label="Your profile">
          <span aria-hidden="true">{initialsFor(displayName)}</span>
        </Link>
      ) : (
        <span className={styles.spacer} aria-hidden="true" />
      )}
    </header>
  )
}

function HamburgerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  )
}
